import { useMemo, useState } from "react";
import { store, useTick } from "../../plc-sim/store";
import { emptyCell, emptyRow, newNetwork, type Cell, type ElemType, type Network } from "../../plc-sim/types";
import { BOXES, COILS } from "../../plc-sim/ladder-utils";
import { tagName } from "../../plc-sim/tags";

const CH = 46;
const EMPTY_W = 36;
const ELEM_W = 74;
const BOX_W = 94;

export type ToolAction = "HLINE" | "VLINE" | "CLEAR";

export type ToolSpec = {
  id: string;
  label: string;
  hint: string;
  group: string;
  type?: ElemType;
  op?: string;
  action?: ToolAction;
};

/**
 * TIA-Portal-style basic instruction library shared by LAD and FBD.
 * The simulator intentionally keeps the same Cell model in both languages so
 * a program can be edited in either view and monitored by the same scan cycle.
 */
export const TOOL_SPECS: ToolSpec[] = [
  { id: "hline", label: "—", hint: "Horizontal wire / خط أفقي", group: "عام / General", action: "HLINE" },
  { id: "vline", label: "│", hint: "Vertical branch / فرع رأسي", group: "عام / General", action: "VLINE" },
  { id: "clear", label: "✕", hint: "Clear cell / مسح العنصر", group: "عام / General", action: "CLEAR" },

  { id: "no", label: "NO", hint: "Normally open contact / نقطة مفتوحة", group: "منطق البت / Bit logic", type: "NO" },
  { id: "nc", label: "NC", hint: "Normally closed contact / نقطة مغلقة", group: "منطق البت / Bit logic", type: "NC" },
  { id: "p", label: "P", hint: "Rising edge / حافة موجبة", group: "منطق البت / Bit logic", type: "P" },
  { id: "n", label: "N", hint: "Falling edge / حافة سالبة", group: "منطق البت / Bit logic", type: "N" },
  { id: "coil", label: "( )", hint: "Output coil / ملف خرج", group: "منطق البت / Bit logic", type: "COIL" },
  { id: "coil_not", label: "(/)", hint: "Negated coil / ملف معكوس", group: "منطق البت / Bit logic", type: "COIL_NOT" },
  { id: "set", label: "(S)", hint: "Set output / تثبيت", group: "منطق البت / Bit logic", type: "SET" },
  { id: "reset", label: "(R)", hint: "Reset output / تصفير", group: "منطق البت / Bit logic", type: "RESET" },
  { id: "sr", label: "SR", hint: "Set/Reset latch - reset dominant", group: "منطق البت / Bit logic", type: "SR" },
  { id: "rs", label: "RS", hint: "Reset/Set latch - set dominant", group: "منطق البت / Bit logic", type: "RS" },
  ...["AND", "OR", "XOR", "NOT", "NAND", "NOR", "XNOR"].map(
    (op): ToolSpec => ({ id: `bool_${op.toLowerCase()}`, label: op, hint: `${op} Boolean function`, group: "منطق البت / Bit logic", type: "BOOL", op }),
  ),

  { id: "ton", label: "TON", hint: "On-delay timer / تأخير تشغيل", group: "المؤقتات / Timers", type: "TON" },
  { id: "tof", label: "TOF", hint: "Off-delay timer / تأخير إطفاء", group: "المؤقتات / Timers", type: "TOF" },
  { id: "tp", label: "TP", hint: "Pulse timer / نبضة زمنية", group: "المؤقتات / Timers", type: "TP" },
  { id: "tonr", label: "TONR", hint: "Retentive on-delay / مؤقت احتفاظي", group: "المؤقتات / Timers", type: "TONR" },

  { id: "ctu", label: "CTU", hint: "Count up / عد تصاعدي", group: "العدادات / Counters", type: "CTU" },
  { id: "ctd", label: "CTD", hint: "Count down / عد تنازلي", group: "العدادات / Counters", type: "CTD" },
  { id: "ctud", label: "CTUD", hint: "Count up/down / عد تصاعدي وتنازلي", group: "العدادات / Counters", type: "CTUD" },

  { id: "eq", label: "==", hint: "Equal compare", group: "المقارنة / Compare", type: "CMP", op: "==" },
  { id: "ne", label: "<>", hint: "Not equal compare", group: "المقارنة / Compare", type: "CMP", op: "<>" },
  { id: "gt", label: ">", hint: "Greater than", group: "المقارنة / Compare", type: "CMP", op: ">" },
  { id: "ge", label: ">=", hint: "Greater or equal", group: "المقارنة / Compare", type: "CMP", op: ">=" },
  { id: "lt", label: "<", hint: "Less than", group: "المقارنة / Compare", type: "CMP", op: "<" },
  { id: "le", label: "<=", hint: "Less or equal", group: "المقارنة / Compare", type: "CMP", op: "<=" },
  { id: "in_range", label: "IN_RANGE", hint: "Value inside limits / داخل المجال", group: "المقارنة / Compare", type: "CMP", op: "IN_RANGE" },
  { id: "out_range", label: "OUT_RANGE", hint: "Value outside limits / خارج المجال", group: "المقارنة / Compare", type: "CMP", op: "OUT_RANGE" },

  { id: "move", label: "MOVE", hint: "Move value / نقل قيمة", group: "النقل / Move", type: "MOVE", op: "MOVE" },
  { id: "swap", label: "SWAP", hint: "Swap bytes in word / تبديل البايتات", group: "النقل / Move", type: "MOVE", op: "SWAP" },
  { id: "sel", label: "SEL", hint: "Binary selection / اختيار بين قيمتين", group: "النقل / Move", type: "SELECT", op: "SEL" },
  { id: "limit", label: "LIMIT", hint: "Limit value / حصر قيمة", group: "النقل / Move", type: "LIMIT" },

  ...["ADD", "SUB", "MUL", "DIV", "MOD", "MIN", "MAX", "ABS", "NEG", "INC", "DEC", "SQRT", "SQR", "EXP", "LN", "LOG", "SIN", "COS", "TAN", "ASIN", "ACOS", "ATAN"].map(
    (op): ToolSpec => ({ id: `math_${op.toLowerCase()}`, label: op, hint: `${op} math function`, group: "الرياضيات / Math", type: "MATH", op }),
  ),

  { id: "round", label: "ROUND", hint: "Round value", group: "التحويل / Conversion", type: "CONVERT", op: "ROUND" },
  { id: "trunc", label: "TRUNC", hint: "Truncate value", group: "التحويل / Conversion", type: "CONVERT", op: "TRUNC" },
  { id: "ceil", label: "CEIL", hint: "Round toward +infinity", group: "التحويل / Conversion", type: "CONVERT", op: "CEIL" },
  { id: "floor", label: "FLOOR", hint: "Round toward -infinity", group: "التحويل / Conversion", type: "CONVERT", op: "FLOOR" },
  { id: "norm_x", label: "NORM_X", hint: "Normalize value / تطبيع", group: "التحويل / Conversion", type: "CONVERT", op: "NORM_X" },
  { id: "scale_x", label: "SCALE_X", hint: "Scale value / تحجيم", group: "التحويل / Conversion", type: "CONVERT", op: "SCALE_X" },

  ...["AND", "OR", "XOR", "NOT"].map(
    (op): ToolSpec => ({ id: `word_${op.toLowerCase()}`, label: `${op}_W`, hint: `${op} word logic`, group: "منطق الكلمات / Word logic", type: "WORD_LOGIC", op }),
  ),
  ...["SHL", "SHR", "ROL", "ROR"].map(
    (op): ToolSpec => ({ id: `shift_${op.toLowerCase()}`, label: op, hint: `${op} shift/rotate`, group: "الإزاحة والدوران / Shift & rotate", type: "WORD_LOGIC", op }),
  ),

  { id: "nop", label: "NOP", hint: "No operation", group: "التحكم بالبرنامج / Program control", type: "PROGRAM", op: "NOP" },
  { id: "label", label: "LABEL", hint: "Network label / تسمية", group: "التحكم بالبرنامج / Program control", type: "PROGRAM", op: "LABEL" },
  { id: "jmp", label: "JMP", hint: "Jump marker (visual in simulator)", group: "التحكم بالبرنامج / Program control", type: "PROGRAM", op: "JMP" },
  { id: "jmpn", label: "JMPN", hint: "Conditional jump marker (visual in simulator)", group: "التحكم بالبرنامج / Program control", type: "PROGRAM", op: "JMPN" },
  { id: "return", label: "RETURN", hint: "Return / إنهاء المسار", group: "التحكم بالبرنامج / Program control", type: "PROGRAM", op: "RETURN" },
];

/** Kept for compatibility with older code that imported the old ladder toolbar. */
export const TOOLS = TOOL_SPECS.filter((t) => t.type).map((t) => ({
  type: t.type!,
  label: t.label,
  hint: t.hint,
}));

export const GROUP_ORDER = [
  "عام / General",
  "منطق البت / Bit logic",
  "المؤقتات / Timers",
  "العدادات / Counters",
  "المقارنة / Compare",
  "الرياضيات / Math",
  "النقل / Move",
  "التحويل / Conversion",
  "التحكم بالبرنامج / Program control",
  "منطق الكلمات / Word logic",
  "الإزاحة والدوران / Shift & rotate",
];

function cellWidth(cell: Cell) {
  if (cell.type === "EMPTY") return EMPTY_W;
  if (BOXES.includes(cell.type)) return BOX_W;
  return ELEM_W;
}

function visibleRowCount(net: Network) {
  let last = 0;
  for (let r = 0; r < net.rows.length; r++) {
    if (net.rows[r]?.some((cell) => cell.type !== "EMPTY" || Boolean(cell.operand))) last = Math.max(last, r);
  }
  for (let r = 0; r < net.links.length; r++) {
    if (net.links[r]?.some(Boolean)) last = Math.max(last, r + 1);
  }
  return Math.min(net.rows.length, last + 1);
}

function visibleColCount(net: Network, rows: Cell[][]) {
  const maxLen = Math.max(1, ...rows.map((row) => row.length));
  let last = -1;
  for (const row of rows) {
    row.forEach((cell, c) => {
      if (cell.type !== "EMPTY" || Boolean(cell.operand)) last = Math.max(last, c);
    });
  }
  net.links.forEach((linkRow) => {
    linkRow?.forEach((on, c) => {
      if (on) last = Math.max(last, c);
    });
  });
  // TIA-like compactness: only show used columns plus one insertion slot.
  return Math.min(maxLen, Math.max(2, last + 2));
}

function lineColor(monitoring: boolean, powered: boolean) {
  return monitoring && powered ? "var(--lad-live)" : "var(--lad-wire)";
}

function CellSymbol({
  cell,
  width,
  powerIn,
  active,
  monitoring,
}: {
  cell: Cell;
  width: number;
  powerIn: boolean;
  active: boolean;
  monitoring: boolean;
}) {
  const cx = width / 2;
  const cy = CH / 2;
  const hot = monitoring && powerIn;
  const leftColor = lineColor(monitoring, powerIn);
  const outHot = monitoring && powerIn && (active || cell.type === "EMPTY" || COILS.includes(cell.type));
  const rightColor = outHot ? "var(--lad-live)" : "var(--lad-wire)";
  const elemColor = monitoring && active ? "var(--lad-live)" : "var(--lad-elem)";
  const t = cell.type;
  const isContact = ["NO", "NC", "P", "N", "CMP"].includes(t);
  const isBox = BOXES.includes(t);

  const leftGap = t === "EMPTY" ? 0 : isBox ? 30 : 20;
  const rightGap = t === "EMPTY" ? 0 : isBox ? 30 : 20;

  const blockLabel =
    ["MATH", "WORD_LOGIC", "CONVERT", "BOOL", "SELECT", "PROGRAM", "MOVE"].includes(t)
      ? cell.op || t
      : t;

  return (
    <svg width={width} height={CH} className="block overflow-visible">
      <line x1={0} y1={cy} x2={Math.max(0, cx - leftGap)} y2={cy} stroke={leftColor} strokeWidth={2.4} />
      <line x1={Math.min(width, cx + rightGap)} y1={cy} x2={width} y2={cy} stroke={rightColor} strokeWidth={2.4} />

      {t === "EMPTY" && <line x1={0} y1={cy} x2={width} y2={cy} stroke={leftColor} strokeWidth={2.4} />}

      {isContact && (
        <g stroke={elemColor} strokeWidth={2.4} fill="none" strokeLinecap="round">
          <line x1={cx - 10} y1={cy - 10} x2={cx - 10} y2={cy + 10} />
          <line x1={cx + 10} y1={cy - 10} x2={cx + 10} y2={cy + 10} />
          <line x1={cx - 20} y1={cy} x2={cx - 10} y2={cy} stroke={leftColor} />
          <line x1={cx + 10} y1={cy} x2={cx + 20} y2={cy} stroke={rightColor} />
          {t === "NC" && <line x1={cx - 12} y1={cy + 12} x2={cx + 12} y2={cy - 12} />}
          {(t === "P" || t === "N") && (
            <text x={cx} y={cy + 4} fontSize={12} textAnchor="middle" stroke="none" fill={elemColor}>
              {t}
            </text>
          )}
          {t === "CMP" && (
            <text x={cx} y={cy + 4} fontSize={10} textAnchor="middle" stroke="none" fill={elemColor}>
              {cell.op ?? ">"}
            </text>
          )}
        </g>
      )}

      {COILS.includes(t) && (
        <g stroke={elemColor} strokeWidth={2.4} fill="none" strokeLinecap="round">
          {/* TIA-style coil parentheses: both curves meet the wire at their outer midpoints. */}
          <path d={`M${cx - 8},${cy - 12} C${cx - 20},${cy - 8} ${cx - 20},${cy + 8} ${cx - 8},${cy + 12}`} />
          <path d={`M${cx + 8},${cy - 12} C${cx + 20},${cy - 8} ${cx + 20},${cy + 8} ${cx + 8},${cy + 12}`} />
          <line x1={cx - 20} y1={cy} x2={cx - 17} y2={cy} stroke={leftColor} />
          <line x1={cx + 17} y1={cy} x2={cx + 20} y2={cy} stroke={rightColor} />
          <text x={cx} y={cy + 4} fontSize={11} textAnchor="middle" stroke="none" fill={elemColor}>
            {t === "SET" ? "S" : t === "RESET" ? "R" : t === "COIL_NOT" ? "/" : ""}
          </text>
        </g>
      )}

      {isBox && (
        <g>
          <rect
            x={cx - 29}
            y={6}
            width={58}
            height={CH - 12}
            rx={3}
            fill="var(--lad-box)"
            stroke={elemColor}
            strokeWidth={2}
          />
          <text x={cx} y={20} fontSize={9.5} fontWeight={600} textAnchor="middle" fill="var(--lad-elem)">
            {blockLabel}
          </text>
          <text x={cx} y={34} fontSize={8} textAnchor="middle" fill="var(--lad-elem)">
            {t === "TON" || t === "TOF" || t === "TP" || t === "TONR" || t === "CTU" || t === "CTD"
              ? cell.preset ?? ""
              : ""}
          </text>
        </g>
      )}

      {hot && monitoring && t !== "EMPTY" && (
        <circle cx={4} cy={cy} r={2.6} fill="var(--lad-live)" />
      )}
    </svg>
  );
}

export function defaultOperand(type: ElemType) {
  if (["COIL", "COIL_NOT", "SET", "RESET", "SR", "RS", "BOOL"].includes(type)) return "M0.0";
  if (["TON", "TOF", "TP", "TONR"].includes(type)) return "T1";
  if (["CTU", "CTD", "CTUD"].includes(type)) return "C1";
  if (["MOVE", "MATH", "WORD_LOGIC", "LIMIT", "CONVERT", "SELECT"].includes(type)) return "MW10";
  if (type === "CMP") return "MW0";
  if (type === "PROGRAM") return "LBL1";
  return "I0.0";
}

export function makeCell(spec: ToolSpec, current: Cell): Cell {
  const type = spec.type ?? "EMPTY";
  const operand = current.operand || defaultOperand(type);

  if (type === "EMPTY") return emptyCell();
  if (["TON", "TOF", "TP"].includes(type)) return { type, operand, preset: current.preset || "5s" };
  if (type === "TONR") return { type, operand, preset: current.preset || "5s", reset: current.reset || "M0.0" };
  if (["CTU", "CTD"].includes(type)) return { type, operand, preset: current.preset || "5", reset: current.reset || "M0.0" };
  if (type === "CTUD") return { type, operand, preset: current.preset || "5", b: current.b || "I0.1", reset: current.reset || "M0.0" };
  if (type === "SR" || type === "RS") return { type, operand, preset: current.preset || "I0.0", b: current.b || "I0.1" };
  if (type === "BOOL") {
    const unary = (spec.op || "") === "NOT";
    return { type, operand, op: spec.op || current.op || "AND", preset: current.preset || "I0.0", b: unary ? undefined : current.b || "I0.1", c: current.c };
  }
  if (type === "CMP") {
    const op = spec.op || current.op || ">";
    if (op === "IN_RANGE" || op === "OUT_RANGE") return { type, operand, op, b: current.b || "0", c: current.c || "100" };
    return { type, operand, op, b: current.b || "0" };
  }
  if (type === "MOVE") return { type, operand, op: spec.op || current.op || "MOVE", preset: current.preset || "MW0" };
  if (type === "SELECT") return { type, operand, op: "SEL", reset: current.reset || "I0.0", preset: current.preset || "0", b: current.b || "100" };
  if (type === "MATH") {
    const unary = ["ABS", "NEG", "INC", "DEC", "SQRT", "SQR", "EXP", "LN", "LOG", "SIN", "COS", "TAN", "ASIN", "ACOS", "ATAN"].includes(spec.op || "");
    return { type, operand, op: spec.op || current.op || "ADD", preset: current.preset || "MW0", b: unary ? undefined : current.b || "1" };
  }
  if (type === "WORD_LOGIC") {
    const unary = (spec.op || "") === "NOT";
    return { type, operand, op: spec.op || current.op || "AND", preset: current.preset || "MW0", b: unary ? undefined : current.b || "1" };
  }
  if (type === "LIMIT") return { type, operand, preset: current.preset || "0", b: current.b || "MW0", c: current.c || "100" };
  if (type === "CONVERT") {
    const op = spec.op || current.op || "ROUND";
    if (op === "NORM_X" || op === "SCALE_X") return { type, operand, op, preset: current.preset || "MW0", b: current.b || "0", c: current.c || "100" };
    return { type, operand, op, preset: current.preset || "MW0" };
  }
  if (type === "PROGRAM") return { type, operand, op: spec.op || current.op || "NOP" };
  return { type, operand };
}

function ToolPreview({ spec }: { spec: ToolSpec }) {
  const width = spec.type && BOXES.includes(spec.type) ? 78 : 64;

  if (spec.action === "HLINE") {
    return (
      <svg width={64} height={36} viewBox="0 0 64 36" aria-hidden="true">
        <line x1={5} y1={18} x2={59} y2={18} stroke="var(--lad-elem)" strokeWidth={2.6} strokeLinecap="round" />
      </svg>
    );
  }

  if (spec.action === "VLINE") {
    return (
      <svg width={64} height={36} viewBox="0 0 64 36" aria-hidden="true">
        <line x1={8} y1={18} x2={56} y2={18} stroke="var(--lad-elem)" strokeWidth={2.4} />
        <line x1={32} y1={4} x2={32} y2={32} stroke="var(--lad-elem)" strokeWidth={2.6} />
      </svg>
    );
  }

  if (spec.action === "CLEAR") {
    return (
      <svg width={64} height={36} viewBox="0 0 64 36" aria-hidden="true">
        <line x1={22} y1={9} x2={42} y2={29} stroke="var(--lad-elem)" strokeWidth={2.6} strokeLinecap="round" />
        <line x1={42} y1={9} x2={22} y2={29} stroke="var(--lad-elem)" strokeWidth={2.6} strokeLinecap="round" />
      </svg>
    );
  }

  if (!spec.type) return null;

  const preview: Cell = { ...emptyCell(), type: spec.type, operand: "" };
  if (spec.op) preview.op = spec.op;
  if (["TON", "TOF", "TP", "TONR"].includes(spec.type)) preview.preset = "PT";

  return (
    <div className="flex h-10 items-center justify-center overflow-hidden" aria-hidden="true">
      <CellSymbol cell={preview} width={width} powerIn={false} active={false} monitoring={false} />
    </div>
  );
}

function ensureColumn(net: Network, c: number) {
  const currentCols = Math.max(1, ...net.rows.map((row) => row.length));
  const required = Math.max(currentCols, c + 1);
  for (const row of net.rows) while (row.length < required) row.push(emptyCell());
  for (let r = 0; r < Math.max(0, net.rows.length - 1); r++) {
    net.links[r] ??= [];
    while (net.links[r]!.length < required + 1) net.links[r]!.push(false);
  }
}

function keepSpareColumn(net: Network, usedColumn: number) {
  const cols = Math.max(1, ...net.rows.map((row) => row.length));
  if (usedColumn < cols - 1) return;
  for (const row of net.rows) row.push(emptyCell());
  for (let r = 0; r < Math.max(0, net.rows.length - 1); r++) {
    net.links[r] ??= [];
    while (net.links[r]!.length < cols + 2) net.links[r]!.push(false);
  }
}

export function LadderView({ tool }: { tool: ElemType | null }) {
  useTick();
  const project = store.project;
  const monitoring = store.status === "RUN";
  const [edit, setEdit] = useState<{ net: string; r: number; c: number } | null>(null);
  const [search, setSearch] = useState("");

  const editing = edit
    ? project.networks.find((n) => n.id === edit.net)?.rows[edit.r]?.[edit.c]
    : null;

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const specs = q
      ? TOOL_SPECS.filter((s) => `${s.label} ${s.hint} ${s.group}`.toLowerCase().includes(q))
      : TOOL_SPECS;
    return GROUP_ORDER.map((group) => ({ group, items: specs.filter((s) => s.group === group) })).filter((g) => g.items.length);
  }, [search]);

  const applySpec = (netId: string, r: number, c: number, spec: ToolSpec) => {
    store.update((p) => {
      const net = p.networks.find((n) => n.id === netId);
      if (!net) return;
      ensureColumn(net, c);

      if (spec.action === "VLINE") {
        if (r >= net.rows.length - 1) {
          const cols = Math.max(1, ...net.rows.map((row) => row.length));
          net.rows.push(emptyRow(cols));
          net.links.push(Array.from({ length: cols + 1 }, () => false));
        }
        net.links[r] ??= Array.from({ length: net.rows[r]!.length + 1 }, () => false);
        net.links[r]![c] = !net.links[r]![c];
        return;
      }

      if (spec.action === "HLINE" || spec.action === "CLEAR") {
        net.rows[r]![c] = emptyCell();
        return;
      }

      if (spec.type) {
        const current = net.rows[r]![c] ?? emptyCell();
        net.rows[r]![c] = makeCell(spec, current);
        keepSpareColumn(net, c);
      }
    });
    setEdit({ net: netId, r, c });
  };

  const place = (netId: string, r: number, c: number) => {
    if (tool) {
      const spec = TOOL_SPECS.find((s) => s.type === tool) ?? { id: tool, label: tool, hint: tool, group: "", type: tool };
      applySpec(netId, r, c, spec);
      return;
    }
    setSearch("");
    setEdit({ net: netId, r, c });
  };

  const patchEditing = (patch: Partial<Cell>, history = false) => {
    if (!edit) return;
    store.update((p) => {
      const net = p.networks.find((n) => n.id === edit.net);
      const cell = net?.rows[edit.r]?.[edit.c];
      if (cell) Object.assign(cell, patch);
    }, history);
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <div className="flex flex-1 flex-col gap-2 overflow-auto p-2" dir="ltr">
        {project.networks.map((net, ni) => {
          const pw = store.power[net.id];
          const rowCount = visibleRowCount(net);
          const visibleRows = net.rows.slice(0, Math.max(1, rowCount));
          const visibleCols = visibleColCount(net, visibleRows);
          const colWidths = Array.from({ length: visibleCols }, (_, c) =>
            Math.max(...visibleRows.map((row) => cellWidth(row[c] ?? emptyCell()))),
          );
          const totalWidth = colWidths.reduce((a, b) => a + b, 0);

          return (
            <section key={net.id} className="max-w-full rounded-lg border border-border bg-card">
              <header className="flex flex-wrap items-center gap-1.5 border-b border-border px-2 py-1">
                <input
                  className="sim-input min-w-32 flex-1 font-medium"
                  value={net.title}
                  onChange={(e) =>
                    store.update((p) => {
                      const n = p.networks.find((x) => x.id === net.id);
                      if (n) n.title = e.target.value;
                    }, false)
                  }
                />
                {monitoring && <span className="sim-chip border-[var(--lad-live)] text-[var(--lad-live)]">MONITOR</span>}
                <button
                  className="sim-btn"
                  onClick={() =>
                    store.update((p) => {
                      const n = p.networks.find((x) => x.id === net.id);
                      if (!n) return;
                      const cols = Math.max(1, ...n.rows.map((row) => row.length));
                      n.rows.push(emptyRow(cols));
                      n.links.push(Array.from({ length: cols + 1 }, () => false));
                    })
                  }
                >
                  + صف
                </button>
                <button
                  className="sim-btn text-destructive"
                  onClick={() => store.update((p) => { p.networks = p.networks.filter((x) => x.id !== net.id); })}
                >
                  حذف
                </button>
              </header>

              <div className="overflow-x-auto px-2 py-1.5">
                <div className="relative inline-block" style={{ minWidth: totalWidth }}>
                  <div
                    className="absolute left-0 top-0 z-10 w-[3px] bg-[var(--lad-rail)]"
                    style={{ height: visibleRows.length * CH }}
                  />
                  <div
                    className="absolute right-0 top-0 z-10 w-[3px] bg-[var(--lad-rail)]"
                    style={{ height: visibleRows.length * CH }}
                  />

                  {visibleRows.map((row, r) => (
                    <div key={r} className="relative flex items-center" style={{ height: CH }}>
                      {Array.from({ length: visibleCols }, (_, c) => {
                        const cell = row[c] ?? emptyCell();
                        const width = colWidths[c]!;
                        const linkOn = Boolean(net.links[r]?.[c]);
                        return (
                          <div key={c} className="relative shrink-0" style={{ width, height: CH }}>
                            <button
                              onClick={() => place(net.id, r, c)}
                              className={`block touch-manipulation rounded-sm active:bg-secondary/80 ${edit?.net === net.id && edit.r === r && edit.c === c ? "bg-primary/10 ring-1 ring-primary/40" : "hover:bg-secondary/50"}`}
                              style={{ width, height: CH }}
                              title={cell.operand || "اضغط للإضافة"}
                            >
                              <CellSymbol
                                cell={cell}
                                width={width}
                                powerIn={Boolean(pw?.power[r]?.[c])}
                                active={Boolean(pw?.cellOn[r]?.[c])}
                                monitoring={monitoring}
                              />
                            </button>

                            {cell.type !== "EMPTY" && (
                              <span className="pointer-events-none absolute inset-x-0 top-0 z-10 truncate px-0.5 text-center font-mono text-[9px] leading-tight text-foreground">
                                {tagName(cell.operand) && <span className="block truncate text-[8px] text-primary">{tagName(cell.operand)}</span>}
                                <span className="block truncate">{cell.operand}</span>
                              </span>
                            )}

                            {r < visibleRows.length - 1 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  applySpec(net.id, r, c, TOOL_SPECS.find((s) => s.action === "VLINE")!);
                                }}
                                className="group absolute -left-[9px] top-1/2 z-20 h-[46px] w-[18px] touch-manipulation"
                                title="خط رأسي / فرع"
                              >
                                <span
                                  className={`mx-auto block h-full w-[3px] rounded transition-opacity ${linkOn ? "opacity-100" : "opacity-0 group-hover:opacity-30 group-focus:opacity-30"}`}
                                  style={{
                                    background: linkOn
                                      ? monitoring && (pw?.power[r]?.[c] || pw?.power[r + 1]?.[c])
                                        ? "var(--lad-live)"
                                        : "var(--lad-elem)"
                                      : "var(--lad-elem)",
                                  }}
                                />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              <div className="px-2 pb-1.5 text-[10px] text-muted-foreground" dir="rtl">
                {net.comment || `شبكة رقم ${ni + 1}`}
              </div>
            </section>
          );
        })}

        <button
          className="sim-btn self-start"
          onClick={() => store.update((p) => { p.networks.push(newNetwork(p.networks.length + 1)); })}
        >
          + شبكة جديدة
        </button>
      </div>

      {edit && editing && (
        <div
          className="absolute right-2 top-2 z-40 flex max-h-[min(78vh,36rem)] w-[min(27rem,calc(100%-1rem))] flex-col overflow-hidden rounded-lg border border-border bg-card/95 shadow-2xl backdrop-blur"
          dir="rtl"
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-2 py-1.5">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold">{editing.type === "EMPTY" ? "إضافة عنصر Ladder" : "خصائص / استبدال العنصر"}</div>
              <div className="truncate font-mono text-[9px] text-muted-foreground">R{edit.r + 1} · C{edit.c + 1} · {editing.type}</div>
            </div>
            <button className="sim-btn" onClick={() => setEdit(null)}>إغلاق</button>
          </div>

          <div className="shrink-0 space-y-2 border-b border-border p-2">
            {editing.type !== "EMPTY" && (
              <>
                <div className="grid grid-cols-2 gap-1.5">
                  <label className="text-[10px]">
                    <span className="mb-1 block text-muted-foreground">العنوان / Operand</span>
                    <input
                      className="sim-input w-full font-mono"
                      value={editing.operand}
                      onChange={(e) => patchEditing({ operand: e.target.value.toUpperCase() })}
                    />
                  </label>
                  <label className="text-[10px]">
                    <span className="mb-1 block text-muted-foreground">الاسم الرمزي</span>
                    <input
                      className="sim-input w-full"
                      value={editing.operand ? store.tag(editing.operand) : ""}
                      disabled={!editing.operand}
                      onChange={(e) => editing.operand && store.setTag(editing.operand, e.target.value)}
                    />
                  </label>
                </div>

                {["TON", "TOF", "TP", "TONR"].includes(editing.type) && (
                  <div className="grid grid-cols-2 gap-1.5">
                    <Field label="PT" value={editing.preset ?? ""} onChange={(v) => patchEditing({ preset: v })} />
                    {editing.type === "TONR" && <Field label="Reset" value={editing.reset ?? ""} onChange={(v) => patchEditing({ reset: v.toUpperCase() })} />}
                  </div>
                )}

                {["CTU", "CTD", "CTUD"].includes(editing.type) && (
                  <div className="grid grid-cols-2 gap-1.5">
                    <Field label="PV" value={editing.preset ?? ""} onChange={(v) => patchEditing({ preset: v })} />
                    <Field label="Reset" value={editing.reset ?? ""} onChange={(v) => patchEditing({ reset: v.toUpperCase() })} />
                    {editing.type === "CTUD" && <Field label="CD" value={editing.b ?? ""} onChange={(v) => patchEditing({ b: v.toUpperCase() })} />}
                  </div>
                )}

                {editing.type === "CMP" && (
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                    <Field label="IN / VALUE" value={editing.operand} onChange={(v) => patchEditing({ operand: v.toUpperCase() })} />
                    <label className="text-[10px]">
                      <span className="mb-1 block text-muted-foreground">Compare</span>
                      <select className="sim-input w-full font-mono" value={editing.op ?? ">"} onChange={(e) => patchEditing({ op: e.target.value })}>
                        {["==", "<>", ">", ">=", "<", "<=", "IN_RANGE", "OUT_RANGE"].map((op) => <option key={op} value={op}>{op}</option>)}
                      </select>
                    </label>
                    <Field label={editing.op === "IN_RANGE" || editing.op === "OUT_RANGE" ? "MIN" : "IN2"} value={editing.b ?? ""} onChange={(v) => patchEditing({ b: v.toUpperCase() })} />
                    {(editing.op === "IN_RANGE" || editing.op === "OUT_RANGE") && <Field label="MAX" value={editing.c ?? ""} onChange={(v) => patchEditing({ c: v.toUpperCase() })} />}
                  </div>
                )}

                {(editing.type === "SR" || editing.type === "RS") && (
                  <div className="grid grid-cols-3 gap-1.5">
                    <Field label="S" value={editing.preset ?? ""} onChange={(v) => patchEditing({ preset: v.toUpperCase() })} />
                    <Field label="R" value={editing.b ?? ""} onChange={(v) => patchEditing({ b: v.toUpperCase() })} />
                    <Field label="Q" value={editing.operand} onChange={(v) => patchEditing({ operand: v.toUpperCase() })} />
                  </div>
                )}

                {editing.type === "BOOL" && (
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                    <Field label="IN1" value={editing.preset ?? ""} onChange={(v) => patchEditing({ preset: v.toUpperCase() })} />
                    {editing.op !== "NOT" && <Field label="IN2" value={editing.b ?? ""} onChange={(v) => patchEditing({ b: v.toUpperCase() })} />}
                    {!["NOT"].includes(editing.op ?? "") && <Field label="IN3 اختياري" value={editing.c ?? ""} onChange={(v) => patchEditing({ c: v.toUpperCase() })} />}
                    <Field label="OUT" value={editing.operand} onChange={(v) => patchEditing({ operand: v.toUpperCase() })} />
                  </div>
                )}

                {editing.type === "SELECT" && (
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                    <Field label="G" value={editing.reset ?? ""} onChange={(v) => patchEditing({ reset: v.toUpperCase() })} />
                    <Field label="IN0" value={editing.preset ?? ""} onChange={(v) => patchEditing({ preset: v.toUpperCase() })} />
                    <Field label="IN1" value={editing.b ?? ""} onChange={(v) => patchEditing({ b: v.toUpperCase() })} />
                    <Field label="OUT" value={editing.operand} onChange={(v) => patchEditing({ operand: v.toUpperCase() })} />
                  </div>
                )}

                {editing.type === "MOVE" && (
                  <div className="grid grid-cols-2 gap-1.5">
                    <Field label="IN" value={editing.preset ?? ""} onChange={(v) => patchEditing({ preset: v.toUpperCase() })} />
                    <Field label="OUT" value={editing.operand} onChange={(v) => patchEditing({ operand: v.toUpperCase() })} />
                  </div>
                )}

                {(editing.type === "MATH" || editing.type === "WORD_LOGIC") && (
                  <div className="grid grid-cols-3 gap-1.5">
                    <Field label="IN1" value={editing.preset ?? ""} onChange={(v) => patchEditing({ preset: v.toUpperCase() })} />
                    {!(["ABS", "NEG", "INC", "DEC", "SQRT", "SQR", "EXP", "LN", "LOG", "SIN", "COS", "TAN", "ASIN", "ACOS", "ATAN", "NOT"].includes(editing.op ?? "")) && (
                      <Field label="IN2" value={editing.b ?? ""} onChange={(v) => patchEditing({ b: v.toUpperCase() })} />
                    )}
                    <Field label="OUT" value={editing.operand} onChange={(v) => patchEditing({ operand: v.toUpperCase() })} />
                  </div>
                )}

                {editing.type === "LIMIT" && (
                  <div className="grid grid-cols-2 gap-1.5">
                    <Field label="MIN" value={editing.preset ?? ""} onChange={(v) => patchEditing({ preset: v.toUpperCase() })} />
                    <Field label="IN" value={editing.b ?? ""} onChange={(v) => patchEditing({ b: v.toUpperCase() })} />
                    <Field label="MAX" value={editing.c ?? ""} onChange={(v) => patchEditing({ c: v.toUpperCase() })} />
                    <Field label="OUT" value={editing.operand} onChange={(v) => patchEditing({ operand: v.toUpperCase() })} />
                  </div>
                )}

                {editing.type === "CONVERT" && (
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                    <Field label="IN / VALUE" value={editing.preset ?? ""} onChange={(v) => patchEditing({ preset: v.toUpperCase() })} />
                    {(editing.op === "NORM_X" || editing.op === "SCALE_X") && <Field label="MIN" value={editing.b ?? ""} onChange={(v) => patchEditing({ b: v.toUpperCase() })} />}
                    {(editing.op === "NORM_X" || editing.op === "SCALE_X") && <Field label="MAX" value={editing.c ?? ""} onChange={(v) => patchEditing({ c: v.toUpperCase() })} />}
                    <Field label="OUT" value={editing.operand} onChange={(v) => patchEditing({ operand: v.toUpperCase() })} />
                  </div>
                )}

                {editing.type === "PROGRAM" && (
                  <div className="grid grid-cols-2 gap-1.5">
                    <Field label="Label / Target" value={editing.operand} onChange={(v) => patchEditing({ operand: v.toUpperCase() })} />
                    <div className="rounded border border-border px-2 py-1 text-[10px] text-muted-foreground">{editing.op ?? "NOP"}</div>
                  </div>
                )}
              </>
            )}

            <input
              className="sim-input w-full"
              placeholder="بحث في جميع عناصر Ladder..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            <div className="space-y-2">
              {filteredGroups.map(({ group, items }) => (
                <section key={group}>
                  <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{group}</div>
                  <div className="grid grid-cols-3 gap-1 sm:grid-cols-4">
                    {items.map((spec) => {
                      const selected = spec.type === editing.type && (!spec.op || spec.op === editing.op);
                      return (
                        <button
                          key={spec.id}
                          className={`sim-btn min-h-12 justify-center overflow-hidden px-0.5 py-0.5 text-center ${selected ? "border-primary bg-primary/10 text-primary" : ""}`}
                          title={spec.hint}
                          aria-label={spec.hint}
                          onClick={() => applySpec(edit.net, edit.r, edit.c, spec)}
                        >
                          <ToolPreview spec={spec} />
                          <span className="sr-only">{spec.hint}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </div>

          <div className="shrink-0 border-t border-border px-2 py-1.5 text-[9px] text-muted-foreground">
            اضغط على عنصر لتبديله في نفس المكان، واضغط على الخط الفارغ لإضافة أي عنصر. الخط الرأسي ينشئ فرعًا مثل TIA Portal.
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="text-[10px]">
      <span className="mb-1 block text-muted-foreground">{label}</span>
      <input className="sim-input w-full font-mono" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
